import * as React from "react";
import { ohneStelle as ohneStelleVon } from "./kommentarZuordnung";
import {
    dokumentKommentarService,
    type KommentarFaden,
    type KommentarStelle,
} from "../../../services/dokumentKommentarService";

/**
 * Zustand der Kommentare EINES Dokuments — geteilt von Lese-Viewer und Editor
 * (`PdfDocumentView` hält ihn, beide Ansichten bekommen ihn gereicht).
 *
 * Die Liste ist die Wahrheit vom Server; jede Aktion ersetzt den betroffenen
 * Faden durch die Antwort des Servers (kein optimistisches Raten).
 */
export interface KommentarSteuerung {
    docId: number;
    faeden: KommentarFaden[];
    fehler: string | null;
    setFehler: (f: string | null) => void;
    /** Kommentare ein-/ausgeblendet — je Browser gemerkt. */
    sichtbar: boolean;
    setSichtbar: (s: boolean) => void;
    /** Welcher Faden gerade als Blase offen ist. */
    offen: number | null;
    setOffen: (id: number | null) => void;
    /** Nicht erledigte Fäden. */
    anzahlOffen: number;
    /** Fäden ohne Stelle: Seite beim Umbau gelöscht oder jenseits der Seitenzahl. */
    ohneStelle: KommentarFaden[];
    /** Seitenzahl der gespeicherten Fassung, wie die Ebene sie meldet — `null` = unbekannt. */
    seitenzahl: number | null;
    setSeitenzahl: (n: number | null) => void;
    anlegen: (stelle: KommentarStelle, text: string) => Promise<KommentarFaden | null>;
    antworten: (wurzelId: number, text: string) => Promise<boolean>;
    erledigtSetzen: (id: number, erledigt: boolean) => Promise<void>;
    loeschen: (id: number) => Promise<void>;
    /** `false` = Zuordnung fehlgeschlagen (Fehler steht in `fehler`) — der Aufrufer bietet „erneut“ an. */
    seitenZuordnen: (zuordnung: number[]) => Promise<boolean>;
    neuLaden: () => void;
}

const SICHTBAR_KEY = "kmvs.pdf.kommentareSichtbar";

const leseSichtbar = (): boolean => {
    try {
        return window.localStorage.getItem(SICHTBAR_KEY) !== "0";
    } catch {
        return true;
    }
};

const meldung = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);

export function useDokumentKommentare(docId: number): KommentarSteuerung {
    const [faeden, setFaeden] = React.useState<KommentarFaden[]>([]);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [sichtbar, setSichtbarState] = React.useState(leseSichtbar);
    const [offen, setOffen] = React.useState<number | null>(null);
    const [seitenzahl, setSeitenzahl] = React.useState<number | null>(null);
    const [ladeZaehler, setLadeZaehler] = React.useState(0);

    React.useEffect(() => {
        let alive = true;
        dokumentKommentarService
            .liste(docId)
            .then((l) => alive && setFaeden(l))
            .catch((err) => alive && setFehler(meldung(err, "Kommentare konnten nicht geladen werden.")));
        return () => {
            alive = false;
        };
    }, [docId, ladeZaehler]);

    const setSichtbar = React.useCallback((s: boolean) => {
        setSichtbarState(s);
        try {
            window.localStorage.setItem(SICHTBAR_KEY, s ? "1" : "0");
        } catch {
            /* privates Fenster o. Ä. — dann eben nur für diese Sitzung */
        }
    }, []);

    const ersetze = React.useCallback((f: KommentarFaden) => {
        setFaeden((alt) => (alt.some((x) => x.id === f.id) ? alt.map((x) => (x.id === f.id ? f : x)) : [...alt, f]));
    }, []);

    const anlegen = React.useCallback(
        async (stelle: KommentarStelle, text: string) => {
            setFehler(null);
            try {
                const f = await dokumentKommentarService.anlegen(docId, { ...stelle, text });
                ersetze(f);
                return f;
            } catch (err) {
                setFehler(meldung(err, "Kommentar konnte nicht gespeichert werden."));
                return null;
            }
        },
        [docId, ersetze],
    );

    const antworten = React.useCallback(
        async (wurzelId: number, text: string) => {
            setFehler(null);
            try {
                ersetze(await dokumentKommentarService.antworten(docId, wurzelId, text));
                return true;
            } catch (err) {
                setFehler(meldung(err, "Antwort konnte nicht gespeichert werden."));
                return false;
            }
        },
        [docId, ersetze],
    );

    const erledigtSetzen = React.useCallback(
        async (id: number, erledigt: boolean) => {
            setFehler(null);
            try {
                ersetze(await dokumentKommentarService.aendern(docId, id, { erledigt }));
            } catch (err) {
                setFehler(meldung(err, "Änderung fehlgeschlagen."));
            }
        },
        [docId, ersetze],
    );

    const loeschen = React.useCallback(
        async (id: number) => {
            setFehler(null);
            try {
                await dokumentKommentarService.loeschen(docId, id);
                // Wurzel weg → ganzer Faden weg; Antwort weg → aus ihrem Faden.
                setFaeden((alt) =>
                    alt
                        .filter((f) => f.id !== id)
                        .map((f) =>
                            f.antworten.some((a) => a.id === id)
                                ? { ...f, antworten: f.antworten.filter((a) => a.id !== id) }
                                : f,
                        ),
                );
                setOffen((o) => (o === id ? null : o));
            } catch (err) {
                setFehler(meldung(err, "Löschen fehlgeschlagen."));
            }
        },
        [docId],
    );

    const seitenZuordnen = React.useCallback(
        async (zuordnung: number[]) => {
            setFehler(null);
            try {
                setFaeden(await dokumentKommentarService.seitenZuordnen(docId, zuordnung));
                return true;
            } catch (err) {
                setFehler(meldung(err, "Kommentare konnten den neuen Seiten nicht zugeordnet werden."));
                return false;
            }
        },
        [docId],
    );

    const neuLaden = React.useCallback(() => setLadeZaehler((n) => n + 1), []);

    const anzahlOffen = faeden.filter((f) => !f.erledigt).length;
    const ohneStelle = React.useMemo(() => ohneStelleVon(faeden, seitenzahl), [faeden, seitenzahl]);

    return {
        docId,
        faeden,
        fehler,
        setFehler,
        sichtbar,
        setSichtbar,
        offen,
        setOffen,
        anzahlOffen,
        ohneStelle,
        seitenzahl,
        setSeitenzahl,
        anlegen,
        antworten,
        erledigtSetzen,
        loeschen,
        seitenZuordnen,
        neuLaden,
    };
}
